import * as d3 from 'd3';
import { maxBy } from '../../../shared/utils/arrayUtils.ts';
import { createGraphG, estimateFontSize, getGraphSvg, getTextWidth, GraphG, GraphSvg } from '../../utils/d3Utils.ts';
import { Bar, BarGraphData, BarGroup, LabelPosition } from './barGraphData.ts';
import { reportGraphMargins } from './reportGraphMargins.ts';


const footerLabelGap = 12;
const axisLabelLineHeight = 1.05;
const hoverPanelLabelClearance = 56;

export function drawReportGraph(
    container: HTMLDivElement,
    data: BarGraphData,
    footerLabelsByGroup?: string[][],
    onFooterGroupClick?: (barGroupIndex: number) => void,
) {
    const root = getGraphSvg(container);
    const maxPeriodLabelLines = data.barGroups.reduce(
        (max, barGroup) => Math.max(max, barGroup.periodLabel.split('\n').length),
        1,
    );
    const maxFooterLines = getFooterLabels(data, footerLabelsByGroup).reduce((max, labels) => Math.max(max, labels.length), 0);
    // The axis margin includes the gap before operator labels. Add only their rows here.
    const footerMargin = maxFooterLines * 24;
    const graphWidth = +root.attr('width') - reportGraphMargins.left - reportGraphMargins.right;
    const textSize = maxTextSize(graphWidth);
    // Reserve the label baselines, descenders, and bottom padding even without operator labels.
    const axisLabelMargin = textSize * (1.4 + (maxPeriodLabelLines - 1) * axisLabelLineHeight) + 12;
    const weeklyAxisMargin = data.barGroups.some((barGroup) => barGroup.periodLabel.startsWith('Week ')) ? 28 : 0;
    const graph = createGraphG(root, {
        ...reportGraphMargins,
        bottom: Math.max(reportGraphMargins.bottom, axisLabelMargin) + weeklyAxisMargin + footerMargin,
    });
    const axes = createAxes(root, graph, data, footerLabelsByGroup, onFooterGroupClick);
    drawData(graph, axes, data);
}

function createAxes(
    root: GraphSvg,
    graph: GraphG,
    data: BarGraphData,
    footerLabelsByGroup?: string[][],
    onFooterGroupClick?: (barGroupIndex: number) => void,
) {
    const { width: graphWidth, height: graphHeight, margins: graphMargins } = graph.datum();
    const textSize = maxTextSize(graphWidth);

    const numBars = data.barGroups[0]?.bars.length ?? 1;
    const isNested = numBars > 1;
    const outerPadding = isNested ? 0.10 : 0.4;
    const innerPadding = isNested ? 0.15 : 0.0;

    // Decide which labels are visible
    const periodLabels = data.barGroups.map(bg => bg.periodLabel);
    const longestLabel = maxBy(periodLabels.flatMap((label) => label.split('\n')), it => it.length) ?? '';
    const labelWidth = getTextWidth(graph, '' + longestLabel, textSize);
    const maxLabels = Math.floor((graphWidth - 20) / (labelWidth + 5));
    const labelFrequency = Math.max(Math.ceil(data.barGroups.length / maxLabels), 1);
    const isLabelVisible = (i: number) => (data.barGroups.length - 1 - i) % labelFrequency === 0// The last label is always visible

    const xOuter = d3
        .scaleBand<number>()
        .range([0, graphWidth])
        .domain(periodLabels.map((d, index) => index))
        .padding(outerPadding);
    const xAxis = root.append('g')
        .attr('transform', `translate(${graphMargins.left}, ${graphHeight + graphMargins.top})`)
        .call(d3.axisBottom(xOuter).tickFormat(index => isLabelVisible(index) ? periodLabels[index] : ''));
    xAxis.selectAll<SVGTextElement, number>('text')
        .style('font-size', textSize + 'px');
    xAxis.selectAll<SVGTextElement, number>('.tick text')
        .attr('y', textSize * 1.15)
        .attr('dy', null)
        .each(function (index) {
            const lines = isLabelVisible(index) ? periodLabels[index].split('\n') : [];
            const text = d3.select(this);
            text.text('');
            lines.forEach((line, lineIndex) => {
                text.append('tspan')
                    .attr('x', 0)
                    .attr('dy', lineIndex === 0 ? 0 : `${axisLabelLineHeight}em`)
                    .text(line);
            });
        });

    const footerLabels = getFooterLabels(data, footerLabelsByGroup);
    const footerTextSize = Math.min(textSize * 0.55, 16);
    const maxPeriodLabelLines = periodLabels.reduce(
        (max, label) => Math.max(max, label.split('\n').length),
        1,
    );
    const footerTop =
        graphHeight
        + graphMargins.top
        + textSize * (1.4 + (maxPeriodLabelLines - 1) * axisLabelLineHeight)
        + footerLabelGap;
    const footerLineHeight = 24;
    footerLabels.forEach((labels, index) => {
        if (labels.length === 0) return;
        const x = graphMargins.left + xOuter(index)! + xOuter.bandwidth() / 2;
        if (onFooterGroupClick) {
            root.append('rect')
                .attr('x', graphMargins.left + xOuter(index)!)
                .attr('y', footerTop - footerTextSize)
                .attr('width', xOuter.bandwidth())
                .attr('height', labels.length * footerLineHeight)
                .attr('fill', 'transparent')
                .attr('cursor', 'pointer')
                .on('click', () => onFooterGroupClick(index));
        }
        labels.forEach((label, labelIndex) => {
            const text = root.append('text')
                .attr('x', x)
                .attr('y', footerTop + labelIndex * footerLineHeight)
                .attr('text-anchor', 'middle')
                .attr('font-family', 'Arial')
                .attr('font-size', footerTextSize + 'px')
                .text(label);
            if (onFooterGroupClick) {
                text
                    .attr('cursor', 'pointer')
                    .style('pointer-events', 'none');
            }
        });
    });

    const xInner = d3
        .scaleBand()
        .domain(Array.from({ length: numBars }, (_, i) => i.toString()))
        .range([0, xOuter.bandwidth()])
        .padding(innerPadding);

    const y = d3
        .scaleLinear()
        .range([graphHeight, 0])
        .domain([0, data.maxValue]);
    root.append('g')
        .attr('transform', `translate(${graphMargins.left}, ${graphMargins.top})`)
        .call(d3.axisLeft(y))
        .selectAll('text')
        .style('font-size', textSize + 'px');
    return { xOuter, xInner, y };
}

function getFooterLabels(data: BarGraphData, footerLabelsByGroup?: string[][]): string[][] {
    return data.barGroups.map((barGroup, index) => footerLabelsByGroup?.[index] ?? barGroup.footerLabels ?? []);
}

function drawData(
    graph: GraphG,
    { xOuter, xInner, y }: ReturnType<typeof createAxes>,
    data: BarGraphData,
) {
    const { width: graphWidth } = graph.datum();
    const barWidth = xInner.bandwidth();
    const hoverPanel = graph.append('g')
        .style('display', 'none')
        .style('pointer-events', 'none');

    const barLabels = data.barGroups.flatMap(bg => bg.bars.flatMap(b => b.labels));
    const sectionLabels = data.barGroups.flatMap(bg => bg.bars.flatMap(b => b.sections.flatMap(s => s.labels)));
    const allLabels = [...barLabels, ...sectionLabels].filter(it => it).map(it => it!.text);
    const maxLabel = maxBy(allLabels, it => it.length)!;
    const textSize = Math.min(estimateFontSize(maxLabel, barWidth * 0.9), maxTextSize(graphWidth));

    // Append target value, if provided
    if (data.targetValue !== undefined) {
        const targetY = y(data.targetValue);
        graph.append('line')
            .attr('x1', 0)
            .attr('y1', targetY)
            .attr('x2', graphWidth)
            .attr('y2', targetY)
            .attr('stroke', 'blue')
            .attr('stroke-width', 2);
    }

    // Append each bar group
    data.barGroups.forEach((barGroup, barGroupIndex) => {

        // Append each bar
        barGroup.bars.forEach((bar, barIndex) => {
            const barX = xOuter(barGroupIndex)! + xInner(barIndex.toString())!;
            const barXMiddle = barX + barWidth / 2;
            const barValue = bar.sections.reduce((sum, section) => sum + section.value, 0);
            const barY = y(barValue);
            const barElement = graph.append('g')
                .on('mouseenter', function () {
                    d3.select(this)
                        .selectAll('rect')
                        .attr('stroke', '#222')
                        .attr('stroke-width', 3);
                    showHoverPanel(
                        hoverPanel,
                        graphWidth,
                        barXMiddle,
                        barY,
                        getHoverLines(barGroup, bar, barIndex),
                    );
                })
                .on('mouseleave', function () {
                    d3.select(this)
                        .selectAll('rect')
                        .attr('stroke', null)
                        .attr('stroke-width', null);
                    hoverPanel.style('display', 'none');
                });

            // Append bar sections
            let prevValueSum = 0;
            bar.sections.forEach((section) => {
                const sectionY = y(prevValueSum + section.value);
                const sectionHeight = y(prevValueSum) - sectionY;

                // Append section rectangle
                barElement.append('rect')
                    .attr('x', barX!)
                    .attr('y', sectionY)
                    .attr('width', barWidth)
                    .attr('height', sectionHeight)
                    .attr('fill', section.color);

                // Append section labels
                section.labels?.forEach(label => {
                    if (!hasSpaceForLabel(sectionHeight, textSize, label.position)) return;
                    barElement.append('text')
                        .attr('x', barXMiddle)
                        .attr('y', getTextLocation(sectionY, sectionHeight, textSize, label.position))
                        .attr('text-anchor', 'middle')
                        .attr('font-family', 'Arial')
                        .attr('font-size', textSize + 'px')
                        .text(label.text);
                });

                prevValueSum += section.value;
            });

            // Append bar labels
            bar.labels?.forEach(label => {
                const valueSum = bar.sections.reduce((sum, section) => sum + section.value, 0);
                const barY = y(valueSum);
                const barHeight = y(0) - y(valueSum);
                if (!hasSpaceForLabel(barHeight, textSize, label.position)) return;
                barElement.append('text')
                    .attr('x', barXMiddle)
                    .attr('y', getTextLocation(barY, barHeight, textSize, label.position))
                    .attr('text-anchor', 'middle')
                    .attr('font-family', 'Arial')
                    .attr('font-size', textSize + 'px')
                    .text(label.text);
            });
        });
    });
}

function getHoverLines(barGroup: BarGroup, bar: Bar, barIndex: number): string[] {
    const period = barGroup.periodLabel.replace(/\n/g, ' ');
    const labelTexts = [
        ...(bar.labels ?? []).map((label) => label.text),
        ...bar.sections.flatMap((section) => (section.labels ?? []).map((label) => label.text)),
    ];
    const shiftLabel = labelTexts.find((label) => ['A', 'B', 'N'].includes(label));
    const values = Array.from(new Set(labelTexts.filter((label) => label !== shiftLabel)));
    const total = bar.sections.reduce((sum, section) => sum + section.value, 0);

    return [
        period,
        shiftLabel ? `Shift: ${shiftLabel}` : barGroup.bars.length > 1 ? `Bar ${barIndex + 1}` : '',
        values.length > 0 ? `Values: ${values.join(' / ')}` : `Value: ${formatHoverValue(total)}`,
    ].filter(Boolean);
}

function formatHoverValue(value: number): string {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
}

function showHoverPanel(
    panel: GraphG,
    graphWidth: number,
    barX: number,
    barY: number,
    lines: string[],
) {
    const lineHeight = 22;
    const panelPaddingX = 12;
    const widestLine = lines.reduce((widest, line) => Math.max(widest, line.length), 0);
    const panelWidth = Math.max(150, Math.min(290, widestLine * 8 + panelPaddingX * 2));
    const panelHeight = 18 + lines.length * lineHeight;
    const panelX = Math.max(8, Math.min(graphWidth - panelWidth - 8, barX - panelWidth / 2));
    const panelY = Math.max(8, barY - panelHeight - hoverPanelLabelClearance);

    panel
        .style('display', null)
        .attr('transform', `translate(${panelX}, ${panelY})`);
    panel.selectAll('*').remove();
    panel.append('rect')
        .attr('width', panelWidth)
        .attr('height', panelHeight)
        .attr('rx', 6)
        .attr('fill', '#fff')
        .attr('fill-opacity', 0.96)
        .attr('stroke', '#555');
    lines.forEach((line, index) => {
        panel.append('text')
            .attr('x', panelPaddingX)
            .attr('y', 23 + index * lineHeight)
            .attr('font-family', 'Arial')
            .attr('font-size', index === 0 ? 16 : 14)
            .attr('font-weight', index === 0 ? 700 : 400)
            .attr('fill', '#111')
            .text(line);
    });
    panel.raise();
}

function maxTextSize(graphWidth: number): number {
    return graphWidth > 1000 ? 40 : 30;
}

function getTextLocation(
    barY: number,
    barHeight: number,
    textSize: number,
    labelPosition: LabelPosition,
): number {
    switch (labelPosition) {
        case 'above-top':
            return barY - 0.3 * textSize;
        case 'below-top':
            return barY + 1.1 * textSize;
        case 'below-below-top':
            return barY + 2.4 * textSize;
        case 'above-bottom':
            return barY + barHeight - 0.3 * textSize;
        case 'middle':
            return barY + 0.5 * barHeight + 0.4 * textSize;
        default:
            return barY;
    }
}

function hasSpaceForLabel(
    barHeight: number,
    textSize: number,
    labelPosition: LabelPosition,
): boolean {
    switch (labelPosition) {
        case 'above-top':
            return true;
        case 'below-top':
        case 'above-bottom':
        case 'middle':
            return barHeight > 1.1 * textSize;
        case 'below-below-top':
            return barHeight > 2.4 * textSize;
        default:
            return false;
    }
}
